import { useCrmSharing } from '@/services/sharing'
import { useBusinessOptions } from '@/services/businessOptions'
import { offerCustomerCopy } from '@/services/appCopy'
import { useCustomerTab } from '@/components/customers/useCustomerTab'
import { affectsRecord, latestRequest } from '@/services/workspaceRefresh'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Form, Modal, Nav, Row, Tab } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons'
import { Link, useParams } from 'react-router-dom'
import { AdminPageHero } from '@/components/common/AdminPageHero'
import { Field } from '@/components/common/Field'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ViewportScroll } from '@/components/common/EditTableScroll'
import { CustomerContacts } from '@/components/customers/CustomerContacts'
import { CustomerTaskDialog } from '@/components/customers/CustomerTaskDialog'
import { OrderHistory } from '@/components/forms/CustomerOrderHistory'
import { useAuth } from '@/context/AuthContext'
import { adminRoles, editorRoles } from '@/helpers/roles'
import { useCrmHub } from '@/context/SignalRContext'
import { readError } from '@/services/apiClient'
import { deleteAction, saveAction } from '@/services/actionService'
import { deleteCustomer, restoreCustomer, loadCustomer, loadCustomerNotes, loadCustomerOrders, saveCustomerNote, deleteCustomerNote, type CustomerDetail, type CustomerOrder } from '@/services/customerService'
import type { CrmAction, Note } from '@/models/api'

export function LiveCustomerDetail() {
  const { canCopy } = useCrmSharing()
  const { options: businessOptions } = useBusinessOptions()
  const { id = '' } = useParams()
  const { activeTab, selectTab } = useCustomerTab()
  const { session } = useAuth()
  const hasEditorRole = !!session?.role && editorRoles.includes(session.role)
  const { notify, lastEvent } = useCrmHub()
  const [customer, setCustomer] = useState<CustomerDetail | null>(null)
  const canEdit = hasEditorRole && !customer?.deleted
  const [orders, setOrders] = useState<CustomerOrder[]>([])
  const [error, setError] = useState<string | null>(null)
  const [missing, setMissing] = useState(false)
  const [noteOpen, setNoteOpen] = useState(false)
  const [imageOpen, setImageOpen] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [addingAction, setAddingAction] = useState(false)
  const [editingActionId, setEditingActionId] = useState<string | null>(null)
  const [actionText, setActionText] = useState('')
  const [confirmDeleteActionId, setConfirmDeleteActionId] = useState<string | null>(null)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [noteText, setNoteText] = useState('')
  const [confirmDeleteNoteId, setConfirmDeleteNoteId] = useState<string | null>(null)

  const request = useRef(latestRequest())
  useEffect(() => () => { request.current() }, [id])
  function refresh() {
    const current = request.current()
    return Promise.all([loadCustomer(id), loadCustomerOrders(id), loadCustomerNotes(id, 1, 0)]).then(([row, nextOrders, notes]) => {
      if (!current()) return
      setCustomer({
        ...row,
        actions: newestFirst(row.actions, (action) => action.actionDate),
        customerNotes: newestFirst(notes.notes, (item) => item.dateAdded),
      })
      setOrders(newestFirst(nextOrders, (order) => order.placedAt))
    }).catch(caught => { if (current()) throw caught })
  }

  useEffect(() => {
    if (affectsRecord(lastEvent, 'customer', 'customer', id)) void refresh().catch(caught => setError(readError(caught)))
  }, [lastEvent, id])

  async function changeDeleted() {
    if (!customer || !window.confirm(customer.deleted ? 'Restore this customer?' : 'Delete this customer? Open actions must be completed or deleted first.')) return
    setBusy(true); setError(null)
    try { await (customer.deleted ? restoreCustomer(id) : deleteCustomer(id)); await refresh(); if (!customer.deleted) offerCustomerCopy(id, true) }
    catch (caught) { setError(readError(caught)) }
    finally { setBusy(false) }
  }

  async function saveOpenAction(action: CrmAction) {
    const text = actionText.trim()
    if (!text) return
    setBusy(true)
    setError(null)
    try {
      await saveAction({
        id: action.id,
        customerId: action.customerId,
        assignedToId: action.assignedToId,
        actionType: action.actionType,
        actionDetail: text,
        actionDate: action.actionDate,
      })
      setEditingActionId(null)
      await refresh()
    } catch (caught) {
      setError(readError(caught))
    } finally {
      setBusy(false)
    }
  }

  async function removeAction(action: CrmAction) {
    setBusy(true)
    setError(null)
    try {
      await deleteAction(action.id)
      await notify('SendActionDeleted', { actionId: action.id, organisationId: action.customerId, userId: session?.userId })
      setConfirmDeleteActionId(null)
      await refresh()
    } catch (caught) {
      setError(readError(caught))
    } finally {
      setBusy(false)
    }
  }

  async function removeNote(item: Note) {
    if (!customer) return
    setBusy(true)
    setError(null)
    try {
      await deleteCustomerNote(item.id)
      await notify('SendNoteDeleted', { organisationId: customer.id, noteId: item.id, userId: session?.userId })
      setConfirmDeleteNoteId(null)
      await refresh()
    } catch (caught) {
      setError(readError(caught))
    } finally {
      setBusy(false)
    }
  }

  async function saveNoteText(item: Note) {
    const text = noteText.trim()
    if (!text || !customer) return
    setBusy(true)
    setError(null)
    try {
      await saveCustomerNote(customer.id, text, item.id)
      await notify('SendNoteUpdated', { organisationId: customer.id, noteId: item.id, userId: session?.userId, text })
      setEditingNoteId(null)
      await refresh()
    } catch (caught) {
      setError(readError(caught))
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    setCustomer(null)
    setMissing(false)
    setError(null)
    setImageOpen(false)
    refresh()
      .catch((caught) => {
        if (cancelled) return
        const message = readError(caught)
        if (message.toLowerCase().includes('not found')) setMissing(true)
        else setError(message)
      })
    return () => { cancelled = true }
  }, [id])

  async function saveNote(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await saveCustomerNote(id, note)
      await notify('SendNoteAdded', { organisationId: id, userId: session?.userId, text: note })
      setNoteOpen(false)
      setNote('')
      await refresh()
    } catch (caught) {
      setError(readError(caught))
    } finally {
      setBusy(false)
    }
  }

  if (missing) {
    return <Alert variant="warning">Customer not found. <Link to="/customers">Back to customers</Link></Alert>
  }
  if (!customer && !error) return <LoadingSpinner text="Loading customer" size="3x" />

  const address = customer ? [customer.address1, customer.address2, customer.address3, customer.city, customer.county, customer.postcode].filter(Boolean) : []
  const facts = customer ? [
    ['Owner', customer.accountOwnerName || 'Not set'],
    ['Type', customer.customerType || 'Not set'],
    ['Category', customer.category || 'Not set'],
    ['Status', customer.status || (customer.onStop ? 'On stop' : 'Active')],
    ...(businessOptions.useGridReferences ? [['Grid reference', customer.gridRef || 'Not set']] : []),
    ['Discount', customer.discount || 'Not set'],
    ...(businessOptions.useCallDay ? [['Call day', customer.callDay || 'Not set']] : []),
    ...(businessOptions.useDeliveryDay ? [['Delivery day', customer.deliveryDay || 'Not set']] : []),
    ['Preferred contact', customer.preferredContactMethod || 'Not set'],
  ] : []

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}
      {customer && (
        <>
          <AdminPageHero
            title={customer.name}
            summary={[customer.accountNumber, customer.category, customer.city].filter(Boolean).join(' · ') || 'Customer'}
          >
            {hasEditorRole && <Button variant="outline-danger" disabled={busy} onClick={() => void changeDeleted()}>{customer.deleted ? "Restore customer" : "Delete customer"}</Button>}
            {canCopy && !!session?.role && adminRoles.includes(session.role) && <Button variant="outline-secondary" onClick={() => offerCustomerCopy(id, customer.deleted, true)}>Copy to other app</Button>}
            {canEdit && !customer.deleted && <Link className="btn btn-outline-primary" to={`/customers/${id}/edit`}>Edit customer</Link>}
          </AdminPageHero>
          <Row className="g-4">
            <Col lg={4} className="d-flex">
              <Card className="h-100 w-100">
                <Card.Header>Account details</Card.Header>
                <Card.Body>
                  {customer.deleted && <Badge bg="secondary" className="me-1">Deleted</Badge>}
                  <Badge bg={customer.onStop ? 'warning' : 'success'} className="mb-3">{customer.onStop ? 'On stop' : 'Active'}</Badge>
                  <div className="customer-address">
                    <div>
                      <p>{address.length ? address.map((line) => <span key={line}>{line}<br /></span>) : 'No address recorded'}</p>
                      <p>
                        {customer.contactNumber || 'No phone'}
                        <br />
                        {customer.emailAddress ? <a href={`mailto:${customer.emailAddress}`}>{customer.emailAddress}</a> : 'No email'}
                      </p>
                    </div>
                    {customer.imageUrl && (
                      <button type="button" className="customer-image-button" onClick={() => setImageOpen(true)} aria-label="View customer image">
                        <img className="customer-image" src={customer.imageUrl} alt={customer.name} />
                      </button>
                    )}
                  </div>
                  <hr />
                  {facts.map(([label, value]) => (
                    <div className="detail-row" key={label}><span>{label}</span><strong>{value}</strong></div>
                  ))}
                  {customer.notes && <><hr /><p className="mb-0">{customer.notes}</p></>}
                </Card.Body>
              </Card>
            </Col>
            <Col lg={8} className="d-flex">
              <Card className="h-100 w-100">
                <Card.Body>
                  <Tab.Container activeKey={activeTab} onSelect={selectTab}>
                    <Nav variant="tabs">
                      <Nav.Item><Nav.Link eventKey="contact">Contacts</Nav.Link></Nav.Item>
                      <Nav.Item><Nav.Link eventKey="actions">Actions</Nav.Link></Nav.Item>
                      <Nav.Item><Nav.Link eventKey="note">Notes</Nav.Link></Nav.Item>
                      <Nav.Item><Nav.Link eventKey="order">Submissions</Nav.Link></Nav.Item>
                    </Nav>
                    <Tab.Content className="border border-top-0 rounded-bottom-3 p-3">
                    <Tab.Pane eventKey="actions">
                      <Button className="mb-3" onClick={() => setAddingAction(true)}>+ Add action</Button>
                      <ViewportScroll className="activity-scroll">
                        {customer.actions.length === 0 && <p className="text-body-secondary">No actions recorded yet.</p>}
                        {customer.actions.map((action) => {
                          const open = !action.dateClosed
                          const canDelete = open && !!session?.role && (adminRoles.includes(session.role) || customer.accountOwnerId === session.businessUserId)
                          const editing = editingActionId === action.id
                          return (
                            <div className="activity customer-activity" key={action.id}>
                              <div className="d-flex justify-content-between align-items-start gap-2">
                                <Badge bg={action.dateClosed ? 'secondary' : action.status === 'OVERDUE' ? 'danger' : 'primary'}>
                                  {action.dateClosed ? 'Completed' : action.actionType}
                                </Badge>
                                {open && !editing && confirmDeleteActionId !== action.id && (
                                  <span className="note-actions customer-activity__buttons">
                                    {canEdit && <button type="button" className="btn btn-link btn-sm p-0" aria-label="Edit action" disabled={busy} onClick={() => { setEditingActionId(action.id); setActionText(action.actionDetail); setConfirmDeleteActionId(null) }}><FontAwesomeIcon icon={faPen} /></button>}
                                    {canDelete && <button type="button" className="btn btn-link btn-sm p-0 text-danger" aria-label="Delete action" disabled={busy} onClick={() => { setConfirmDeleteActionId(action.id); setEditingActionId(null) }}><FontAwesomeIcon icon={faTrash} /></button>}
                                  </span>
                                )}
                              </div>
                              {editing ? (
                                <Form className="mt-2" onSubmit={(event) => { event.preventDefault(); void saveOpenAction(action) }}>
                                  <Form.Control as="textarea" rows={3} value={actionText} aria-label="Action text" onChange={(event) => setActionText(event.target.value)} />
                                  <div className="d-flex gap-2 mt-2">
                                    <Button type="submit" size="sm" disabled={busy || !actionText.trim()}>Save</Button>
                                    <Button type="button" size="sm" variant="outline-secondary" disabled={busy} onClick={() => setEditingActionId(null)}>Cancel</Button>
                                  </div>
                                </Form>
                              ) : (
                                <p className="mt-2 mb-1">{action.actionDetail}</p>
                              )}
                              <small>{formatWhen(action.actionDate)} · {action.assignedToName || 'Unassigned'}</small>
                              {confirmDeleteActionId === action.id && (
                                <div className="d-flex gap-2 mt-2">
                                  <Button type="button" size="sm" variant="danger" disabled={busy} onClick={() => void removeAction(action)}>Delete action</Button>
                                  <Button type="button" size="sm" variant="outline-secondary" disabled={busy} onClick={() => setConfirmDeleteActionId(null)}>Keep action</Button>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </ViewportScroll>
                    </Tab.Pane>
                    <Tab.Pane eventKey="contact">
                      <CustomerContacts customerId={customer.id} customerName={customer.name} canEdit={canEdit} />
                    </Tab.Pane>
                    <Tab.Pane eventKey="note">
                      <Button className="mb-3" onClick={() => { setNote(''); setNoteOpen(true) }}>+ Add note</Button>
                      <ViewportScroll className="activity-scroll">
                        {customer.customerNotes.length === 0 && <p className="text-body-secondary">No notes recorded yet.</p>}
                        {customer.customerNotes.map((item) => {
                          const canEditNote = !!session?.role && (adminRoles.includes(session.role) || customer.accountOwnerId === session.businessUserId)
                          const editing = editingNoteId === item.id
                          return (
                            <div className={`activity customer-activity ${item.noteType === 'OnStop' ? 'activity--on-stop' : item.noteType === 'StopRemoved' ? 'activity--stop-removed' : ''}`} key={item.id}>
                              <div className="d-flex justify-content-between align-items-start gap-2">
                                <small>
                                  {item.noteType === 'OnStop' ? 'Placed on stop · ' : item.noteType === 'StopRemoved' ? 'Stop removed · ' : ''}
                                  {formatWhen(item.dateAdded)}{item.addedByName ? ` · ${item.addedByName}` : ''}
                                </small>
                                {canEditNote && !editing && confirmDeleteNoteId !== item.id && (
                                  <span className="note-actions customer-activity__buttons">
                                    <button type="button" className="btn btn-link btn-sm p-0" aria-label="Edit note" disabled={busy} onClick={() => { setEditingNoteId(item.id); setNoteText(item.noteText); setConfirmDeleteNoteId(null) }}><FontAwesomeIcon icon={faPen} /></button>
                                    <button type="button" className="btn btn-link btn-sm p-0 text-danger" aria-label="Delete note" disabled={busy} onClick={() => { setConfirmDeleteNoteId(item.id); setEditingNoteId(null) }}><FontAwesomeIcon icon={faTrash} /></button>
                                  </span>
                                )}
                              </div>
                              {editing ? (
                                <Form className="mt-2" onSubmit={(event) => { event.preventDefault(); void saveNoteText(item) }}>
                                  <Form.Control as="textarea" rows={3} value={noteText} aria-label="Note" onChange={(event) => setNoteText(event.target.value)} />
                                  <div className="d-flex gap-2 mt-2">
                                    <Button type="submit" size="sm" disabled={busy || !noteText.trim()}>Save</Button>
                                    <Button type="button" size="sm" variant="outline-secondary" disabled={busy} onClick={() => setEditingNoteId(null)}>Cancel</Button>
                                  </div>
                                </Form>
                              ) : (
                                <p className="mb-0 mt-1">{item.noteText}</p>
                              )}
                              {confirmDeleteNoteId === item.id && (
                                <div className="d-flex gap-2 mt-2">
                                  <Button type="button" size="sm" variant="danger" disabled={busy} onClick={() => void removeNote(item)}>Delete note</Button>
                                  <Button type="button" size="sm" variant="outline-secondary" disabled={busy} onClick={() => setConfirmDeleteNoteId(null)}>Keep note</Button>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </ViewportScroll>
                    </Tab.Pane>
                    <Tab.Pane eventKey="order">
                      <Link className="btn btn-primary mb-3" to={`/orders/new/${id}`}>+ New submission</Link>
                      <ViewportScroll className="activity-scroll">
                        {orders.length === 0 && <p className="text-body-secondary">No submissions recorded yet.</p>}
                        <OrderHistory orders={orders} customer={customer} />
                      </ViewportScroll>
                    </Tab.Pane>
                    </Tab.Content>
                  </Tab.Container>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          {addingAction && (
            <CustomerTaskDialog
              kind="action"
              customer={{ id: customer.id, name: customer.name }}
              onClose={() => setAddingAction(false)}
              onSaved={() => { void refresh() }}
            />
          )}
          <Modal show={imageOpen} onHide={() => setImageOpen(false)} size="lg" className="customer-lightbox">
            <Modal.Body>
              <img src={customer.imageUrl ?? ''} alt={customer.name} />
            </Modal.Body>
          </Modal>
          <Modal show={noteOpen} onHide={() => setNoteOpen(false)}>
            <form onSubmit={(event) => void saveNote(event)}>
              <Modal.Header closeButton><Modal.Title>Add note</Modal.Title></Modal.Header>
              <Modal.Body>
                <Field label="Note" type="textarea" value={note} onChange={setNote} required />
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setNoteOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={busy}>Save note</Button>
              </Modal.Footer>
            </form>
          </Modal>
        </>
      )}
    </>
  )
}

function newestFirst<T>(items: T[], date: (item: T) => string | null | undefined) {
  return [...items].sort((left, right) => {
    const leftTime = Date.parse(date(left) ?? '')
    const rightTime = Date.parse(date(right) ?? '')
    return (Number.isNaN(rightTime) ? 0 : rightTime) - (Number.isNaN(leftTime) ? 0 : leftTime)
  })
}

function formatWhen(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
